import React, { useState } from "react";
import { submitLead, formatApiError } from "../lib/api";
import { Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../lib/i18n";

export default function ContactForm({ defaultLocation = "diez" }) {
  const { t, lp } = useLang();
  const [data, setData] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    preferred_location: defaultLocation, inquiry_type: "", message: "",
    consent: false, private_service_ack: false, hp_field: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setData((d) => ({ ...d, [k]: v }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!data.private_service_ack) {
      toast.error(t("cfPrivateAckError"));
      return;
    }
    if (!data.consent) {
      toast.error(t("cfConsentError"));
      return;
    }
    setLoading(true);
    try {
      await submitLead(data);
      setDone(true);
      toast.success(t("cfSuccessToast"));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="card-elevated p-8 text-center" data-testid="contact-form-success">
        <CheckCircle2 className="h-12 w-12 text-navy mx-auto mb-3" />
        <h3 className="text-2xl font-semibold text-navy-dark mb-2">{t("cfSuccessTitle")}</h3>
        <p className="text-ink-muted">{t("cfSuccessText")}</p>
      </div>
    );
  }

  const input = "h-12 w-full rounded-lg border border-outline bg-white px-4 text-ink placeholder-ink-muted/60 focus:outline-none focus:ring-2 focus:ring-navy/30 focus:border-navy transition";
  const label = "block text-sm font-semibold text-navy-dark mb-2";

  return (
    <>
      <form onSubmit={onSubmit} className="card-elevated p-7 md:p-8 space-y-4" data-testid="contact-form" noValidate>
      <input type="text" name="hp" tabIndex={-1} autoComplete="off" className="hidden" value={data.hp_field} onChange={onChange("hp_field")} aria-hidden />
      <aside className="rounded-xl border-2 border-amber-500 bg-amber-50 p-4 text-amber-950" data-testid="private-service-notice" role="note">
        <p className="font-bold mb-1">{t("cfPrivateTitle")}</p>
        <p className="text-sm leading-relaxed">{t("cfPrivateText")}</p>
      </aside>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className={label} htmlFor="first_name">{t("cfFirstName")} *</label>
          <input id="first_name" required className={input} value={data.first_name} onChange={onChange("first_name")} data-testid="cf-first-name" />
        </div>
        <div>
          <label className={label} htmlFor="last_name">{t("cfLastName")} *</label>
          <input id="last_name" required className={input} value={data.last_name} onChange={onChange("last_name")} data-testid="cf-last-name" />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className={label} htmlFor="email">{t("cfEmail")} *</label>
          <input id="email" type="email" required className={input} value={data.email} onChange={onChange("email")} data-testid="cf-email" />
        </div>
        <div>
          <label className={label} htmlFor="phone">{t("cfPhone")}</label>
          <input id="phone" className={input} value={data.phone} onChange={onChange("phone")} data-testid="cf-phone" />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className={label} htmlFor="loc">{t("cfLocation")} *</label>
          <select id="loc" required className={input} value={data.preferred_location} onChange={onChange("preferred_location")} data-testid="cf-location">
            <option value="diez">Diez</option>
          </select>
        </div>
        <div>
          <label className={label} htmlFor="inq">{t("cfInquiry")}</label>
          <select id="inq" className={input} value={data.inquiry_type} onChange={onChange("inquiry_type")} data-testid="cf-inquiry">
            <option value="">{t("cfChoose")}</option>
            <option value="sportbeginn">{t("cfInqSportbeginn")}</option>
            <option value="wiedereinstieg">{t("cfInqWiedereinstieg")}</option>
            <option value="herzcheck">{t("cfInqHerzcheck")}</option>
            <option value="belastungsbeschwerden">{t("cfInqBelastung")}</option>
            <option value="familiaere_vorbelastung">{t("cfInqFamiliaer")}</option>
            <option value="anderes">{t("cfInqAnderes")}</option>
          </select>
        </div>
      </div>
      <div>
        <label className={label} htmlFor="msg">{t("cfMessage")} *</label>
        <textarea
          id="msg" required rows={5}
          className="w-full rounded-lg border border-outline bg-white px-4 py-3 text-ink placeholder-ink-muted/60 focus:outline-none focus:ring-2 focus:ring-navy/30 focus:border-navy"
          value={data.message} onChange={onChange("message")} data-testid="cf-message"
        />
      </div>
      <label className="flex gap-3 items-start cursor-pointer rounded-lg border border-amber-300 bg-amber-50/70 p-3">
        <input type="checkbox" checked={data.private_service_ack} onChange={onChange("private_service_ack")} required className="mt-1 h-4 w-4 accent-navy" data-testid="cf-private-service-ack" />
        <span className="text-sm font-medium text-ink leading-relaxed">{t("cfPrivateAck")}</span>
      </label>
      <label className="flex gap-3 items-start cursor-pointer">
        <input type="checkbox" checked={data.consent} onChange={onChange("consent")} required className="mt-1 h-4 w-4 accent-navy" data-testid="cf-consent" />
        <span className="text-sm text-ink-muted leading-relaxed">
          {t("cfConsentPre")}<a href={lp("/datenschutz/")} className="text-navy underline">{t("cfConsentLink")}</a>{t("cfConsentPost")}
        </span>
      </label>
      <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="cf-submit">
        {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> {t("cfSending")}</> : t("cfSubmit")}
      </button>
    </form>
    </>
  );
}
